import {
  Table,
  Tooltip,
  Modal,
  Form,
  Descriptions,
  Select,
  notification,
} from "antd";
import { DeleteOutlined, EyeOutlined, FilterFilled } from "@ant-design/icons";
import {
  useConfirmDeleteModal,
  useDebouncedTableFilter,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import {
  useDeleteEdiDetailsMutation,
  useGetEdiDataQuery,
  useGetEdiDetailsQuery,
  usePatchEdiDetailsMutation,
} from "shared/api/ediApi";
import React, { useState, useEffect } from "react";
import { useGetAllCompaniesQuery } from "shared/api";
import { useTranslation } from "react-i18next";
import { handleRequestError } from "shared/helpers";
import { sortDirections } from "shared/constants";
import { MultipleOptionsTableFilter, TableSearchFilter } from "shared/ui";
import classNames from "classnames";

const EdiTableView: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();
  const pagination = usePagination(20);
  const { ordering, changeSort } = useTableSorting();

  const {
    debouncedValue: debouncedFilename,
    handleFilterChange: handleFilenameChange,
    value: filenameValue,
    clearFilterValue: clearFilenameValue,
  } = useDebouncedTableFilter("Filename", 400);

  const {
    debouncedValue: debouncedCompanyName,
    handleFilterChange: handleCompanyNameChange,
    value: companyNameValue,
    clearFilterValue: clearCompanyNameValue,
  } = useDebouncedTableFilter("CompanyName", 400);

  const {
    debouncedValue: debouncedParsingResult,
    handleFilterChange: handleParsingResultChange,
    value: parsingResultValue,
    clearFilterValue: clearParsingResultValue,
  } = useDebouncedTableFilter("ParsingResult", 400);

  const {
    debouncedValue: debouncedParsingMessage,
    handleFilterChange: handleParsingMessageChange,
    value: parsingMessageValue,
    clearFilterValue: clearParsingMessageValue,
  } = useDebouncedTableFilter("ParsingMessage", 400);

  const { t } = useTranslation();
  const { data, refetch: refetchTableData } = useGetEdiDataQuery(
    {
      ordering,
      filename__icontains: debouncedFilename || undefined,
      company__name__icontains: debouncedCompanyName || undefined,
      parsing_result: debouncedParsingResult || undefined,
      parsing_message__icontains: debouncedParsingMessage || undefined,
      expand: "company",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { setCurrentPage, pageSize, currentPage } = pagination;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: details,
    isFetching,
    refetch: refetchDetails,
  } = useGetEdiDetailsQuery({ id: selectedId! }, { skip: !selectedId });

  const { data: companies } = useGetAllCompaniesQuery({});
  const transformedCompanies = companies?.map((c) => ({
    label: c.name,
    value: c.id,
  }));

  const [patchEdiDetails] = usePatchEdiDetailsMutation();
  const [deleteEdiDetails] = useDeleteEdiDetailsMutation();

  const [form] = Form.useForm();

  useEffect(() => {
    if (details) {
      form.setFieldsValue({ company: details.company });
    }
  }, [details, form]);

  const handleOpen = (id: number) => {
    setSelectedId(id);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedId(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (selectedId) {
        await patchEdiDetails({
          id: selectedId,
          company: values.company,
        }).unwrap();
        api.success({
          message: t("EDI details updated"),
        });
      }

      refetchDetails();
      refetchTableData();
      handleClose();
    } catch (error) {
      api.error({
        message: t("Failed to update EDI"),
        description: handleRequestError(error, t("users-page.error-title")),
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteEdiDetails({ id }).unwrap();
      api.success({
        message: t("File deleted successfully"),
      });
      refetchTableData();
    } catch (error) {
      api.error({
        message: t("Failed to delete file"),
        description: handleRequestError(error, t("users-page.error-title")),
      });
    }
  };

  const confirmDelete = useConfirmDeleteModal(
    t("Are you sure you want to delete this file?"),
    (id: number) => handleDelete(id)
  );

  const excluded = [
    "id",
    "filename",
    "company",
    "parsing_result",
    "parsing_message",
  ];

  const rows =
    details &&
    Object.entries(details)
      .filter(([key]) => !excluded.includes(key))
      .map(([key, value]) => ({
        key,
        value,
      }));

  return (
    <>
      {contextHolder}
      <Table
        rowKey="id"
        className="mt-5"
        columns={[
          {
            title: "Filename",
            dataIndex: "filename",
            sorter: true,
            sortDirections,
            onHeaderCell: () => ({
              onClick: () => changeSort("filename", "-filename"),
            }),
            filterIcon: (
              <FilterFilled
                className={classNames({
                  "!text-blue-600 scale-125": filenameValue.length > 0,
                })}
              />
            ),
            filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
              <TableSearchFilter
                setSelectedKeys={setSelectedKeys}
                clearFilters={clearFilenameValue}
                confirm={confirm}
                value={filenameValue}
                handleFilterChange={handleFilenameChange}
                visible={visible}
                placeholder={t("Enter file name") || ""}
                title={t("Filter by filename") || ""}
              />
            ),
          },
          {
            title: "Company",
            dataIndex: "company",
            sorter: true,
            sortDirections,
            onHeaderCell: () => ({
              onClick: () => changeSort("company", "-company"),
            }),
            render: (company: { name?: string }) =>
              company?.name ?? (
                <span style={{ textDecoration: "line-through", color: "#999" }}>
                  —
                </span>
              ),
            filterIcon: (
              <FilterFilled
                className={classNames({
                  "!text-green-600 scale-125": companyNameValue.length > 0,
                })}
              />
            ),
            filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
              <TableSearchFilter
                setSelectedKeys={setSelectedKeys}
                clearFilters={clearCompanyNameValue}
                confirm={confirm}
                value={companyNameValue}
                handleFilterChange={handleCompanyNameChange}
                visible={visible}
                placeholder={t("Enter company name") || ""}
                title={t("Filter by company") || ""}
              />
            ),
          },
          {
            title: "Parsing Result",
            dataIndex: "parsing_result",
            sorter: true,
            sortDirections,
            onHeaderCell: () => ({
              onClick: () => changeSort("parsing_result", "-parsing_result"),
            }),
            render: (value: string) => (
              <span
                className={classNames("capitalize", {
                  "text-green-600": value === "success",
                  "text-red-600": value === "failed" || value === "error",
                })}
              >
                {value || "-"}
              </span>
            ),
            filterDropdown: () => (
              <MultipleOptionsTableFilter
                options={[
                  { label: "Success", value: "success" },
                  { label: "Error", value: "error" },
                ]}
                selectedValues={parsingResultValue ? [parsingResultValue] : []}
                handleChange={([value]) => {
                  const val = String(value || "");
                  handleParsingResultChange(
                    parsingResultValue === val ? "" : val
                  );
                }}
                clearFilters={clearParsingResultValue}
              />
            ),

            filteredValue: parsingResultValue ? [parsingResultValue] : [],
            filterIcon: (
              <FilterFilled
                className={classNames({
                  "!text-orange-600 scale-125": Boolean(parsingResultValue),
                })}
              />
            ),
          },
          {
            title: "Parsing Message",
            dataIndex: "parsing_message",
            filterIcon: (
              <FilterFilled
                className={classNames({
                  "!text-purple-600 scale-125": parsingMessageValue.length > 0,
                })}
              />
            ),
            filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
              <TableSearchFilter
                setSelectedKeys={setSelectedKeys}
                clearFilters={clearParsingMessageValue}
                confirm={confirm}
                value={parsingMessageValue}
                handleFilterChange={handleParsingMessageChange}
                visible={visible}
                placeholder={t("Enter parsing message") || ""}
                title={t("Filter by parsing message") || ""}
              />
            ),
          },
          {
            title: "Actions",
            key: "actions",
            render: (_: any, record: { id: number }) => (
              <div style={{ display: "flex", gap: 12 }}>
                <Tooltip title="View details">
                  <EyeOutlined
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOpen(record.id)}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <DeleteOutlined
                    style={{ cursor: "pointer", color: "red" }}
                    onClick={() => confirmDelete(record.id)}
                  />
                </Tooltip>
              </div>
            ),
          },
        ]}
        dataSource={data ?? []}
        pagination={{
          pageSizeOptions: ["10", "20", "50"],
          current: currentPage,
          pageSize,
          total: data?.length ?? 0,
          showSizeChanger: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            pagination.setPageSize?.(size);
          },
        }}
      />

      <Modal
        open={isModalOpen}
        onCancel={handleClose}
        title="EDI Details"
        onOk={handleSave}
        okButtonProps={{
          style: {
            backgroundColor: "#1677ff",
            color: "#fff",
            borderColor: "#1677ff",
          },
        }}
        width={900}
      >
        {isFetching || !details ? (
          <p>Loading...</p>
        ) : (
          <>
            <Form form={form} layout="vertical">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Filename">
                  {details.filename}
                </Descriptions.Item>
                <Descriptions.Item label="Company">
                  <Form.Item name="company" noStyle>
                    <Select
                      style={{ width: "100%" }}
                      options={transformedCompanies}
                      placeholder="Select company"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Parsing Result">
                  {details.parsing_result}
                </Descriptions.Item>
                <Descriptions.Item label="Parsing Message">
                  {details.parsing_message}
                </Descriptions.Item>
              </Descriptions>
            </Form>

            <Table
              className="mt-4"
              rowKey="key"
              size="small"
              pagination={false}
              columns={[
                { title: "Field", dataIndex: "key" },
                { title: "Value", dataIndex: "value" },
              ]}
              dataSource={rows ?? []}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default EdiTableView;
